import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import ImageUpload from "@/components/site/ImageUpload";

export default function SiteConfigTab() {
  const [cfg, setCfg] = React.useState(null);
  const [consultations, setConsultations] = React.useState([]);
  const [newCat, setNewCat] = React.useState("");
  const [newFaqTheme, setNewFaqTheme] = React.useState("");
  const [newMarquee, setNewMarquee] = React.useState("");
  React.useEffect(() => {
    api.get("/site-config").then((r) => setCfg(r.data));
    api.get("/consultations").then((r) => setConsultations(r.data || [])).catch(() => {});
  }, []);

  if (!cfg) return <div>Chargement…</div>;

  const updateBanner = (k, v) => setCfg((c) => ({ ...c, banner: { ...c.banner, [k]: v } }));
  const updateFab = (k, v) => setCfg((c) => ({ ...c, fab: { ...c.fab, [k]: v } }));
  const cats = cfg.categories?.articles || [];
  const faqThemes = cfg.categories?.faq_themes || [];
  const quickItems = cfg.quick_access || [];
  const pageHeaders = cfg.page_headers || {};
  const marqueeItems = cfg.marquee || [];
  const statsOv = cfg.stats_overrides || { students: null, schools: null, voiced: null };

  const updateStatsOv = (k, v) => setCfg((c) => ({ ...c, stats_overrides: { ...(c.stats_overrides || {}), [k]: v } }));

  const legal = cfg.legal_help || { enabled: true, title: "", subtitle: "", partners: [] };
  const homeSections = cfg.home_sections || {};
  const consultBlock = cfg.consultation_block || { consultation_slug: "", custom_title: "", custom_description: "", custom_cta_label: "Donner mon avis", custom_cta_link: "" };
  const updateHomeSection = (k, v) => setCfg((c) => ({ ...c, home_sections: { ...(c.home_sections || {}), [k]: v } }));
  const updateConsultBlock = (k, v) => setCfg((c) => ({ ...c, consultation_block: { ...(c.consultation_block || {}), [k]: v } }));
  const updateLegal = (k, v) => setCfg((c) => ({ ...c, legal_help: { ...(c.legal_help || {}), [k]: v } }));
  const updateLegalPartner = (idx, k, v) => setCfg((c) => {
    const partners = [...((c.legal_help && c.legal_help.partners) || [])];
    partners[idx] = { ...partners[idx], [k]: v };
    return { ...c, legal_help: { ...(c.legal_help || {}), partners } };
  });
  const addLegalPartner = () => setCfg((c) => {
    const partners = [...((c.legal_help && c.legal_help.partners) || [])];
    partners.push({ name: "Nouveau partenaire", short_name: "", logo_url: "", url: "", button_label: "Plus d'infos", popup_enabled: false, popup_content: "" });
    return { ...c, legal_help: { ...(c.legal_help || {}), partners } };
  });
  const removeLegalPartner = (idx) => setCfg((c) => {
    const partners = [...((c.legal_help && c.legal_help.partners) || [])];
    partners.splice(idx, 1);
    return { ...c, legal_help: { ...(c.legal_help || {}), partners } };
  });

  const updateMarquee = (idx, val) => setCfg((c) => {
    const next = [...(c.marquee || [])];
    next[idx] = val;
    return { ...c, marquee: next };
  });
  const removeMarquee = (idx) => setCfg((c) => {
    const next = [...(c.marquee || [])];
    next.splice(idx, 1);
    return { ...c, marquee: next };
  });
  const addMarquee = () => {
    const v = newMarquee.trim();
    if (!v) return;
    setCfg((c) => ({ ...c, marquee: [...(c.marquee || []), v] }));
    setNewMarquee("");
  };

  const updatePageHeader = (slug, key, val) => setCfg((c) => ({
    ...c,
    page_headers: { ...(c.page_headers || {}), [slug]: { ...(c.page_headers?.[slug] || {}), [key]: val } },
  }));

  const ICONS = ["Map", "BookOpen", "Vote", "Megaphone", "MessageSquare", "Users", "Sparkles", "Calendar", "Heart", "Mic", "FileText", "Globe", "Star"];
  const COLORS = ["pink", "blue", "yellow", "green", "white", "black"];

  const updateQuick = (idx, key, val) => setCfg((c) => {
    const next = [...(c.quick_access || [])];
    next[idx] = { ...next[idx], [key]: val };
    return { ...c, quick_access: next };
  });
  const removeQuick = (idx) => setCfg((c) => {
    const next = [...(c.quick_access || [])];
    next.splice(idx, 1);
    return { ...c, quick_access: next };
  });
  const addQuick = () => setCfg((c) => ({
    ...c,
    quick_access: [...(c.quick_access || []), { label: "Nouvel outil", icon: "Sparkles", color: "white", link: "/" }],
  }));

  const updateCat = (idx, v) => setCfg((c) => {
    const next = [...(c.categories?.articles || [])];
    next[idx] = v;
    return { ...c, categories: { ...c.categories, articles: next } };
  });
  const removeCat = (idx) => setCfg((c) => {
    const next = [...(c.categories?.articles || [])];
    next.splice(idx, 1);
    return { ...c, categories: { ...c.categories, articles: next } };
  });
  const addCat = () => {
    const v = newCat.trim();
    if (!v) return;
    if (cats.includes(v)) return toast.error("Catégorie déjà présente");
    setCfg((c) => ({ ...c, categories: { ...c.categories, articles: [...(c.categories?.articles || []), v] } }));
    setNewCat("");
  };

  const updateFaqTheme = (idx, v) => setCfg((c) => {
    const next = [...(c.categories?.faq_themes || [])];
    next[idx] = v;
    return { ...c, categories: { ...c.categories, faq_themes: next } };
  });
  const removeFaqTheme = (idx) => setCfg((c) => {
    const next = [...(c.categories?.faq_themes || [])];
    next.splice(idx, 1);
    return { ...c, categories: { ...c.categories, faq_themes: next } };
  });
  const addFaqTheme = () => {
    const v = newFaqTheme.trim();
    if (!v) return;
    if (faqThemes.includes(v)) return toast.error("Thématique déjà présente");
    setCfg((c) => ({ ...c, categories: { ...c.categories, faq_themes: [...(c.categories?.faq_themes || []), v] } }));
    setNewFaqTheme("");
  };

  const save = async () => {
    try {
      await api.patch("/admin/site-config", {
        banner: cfg.banner,
        fab: cfg.fab,
        categories: cfg.categories,
        quick_access: cfg.quick_access,
        page_headers: cfg.page_headers,
        marquee: cfg.marquee,
        stats_overrides: cfg.stats_overrides,
        legal_help: cfg.legal_help,
        home_sections: cfg.home_sections,
        consultation_block: cfg.consultation_block,
      });
      toast.success("Configuration enregistrée");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="grid gap-8">
      <section>
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase mb-4">Bannière d'accueil</h2>
        <div className="neo-card p-5 grid sm:grid-cols-2 gap-3" data-testid="site-banner-form">
          <label className="flex items-center gap-2 sm:col-span-2 text-sm font-bold">
            <input type="checkbox" checked={!!cfg.banner.enabled} onChange={(e) => updateBanner("enabled", e.target.checked)} data-testid="banner-enabled" /> Afficher la bannière
          </label>
          <input className="neo-input sm:col-span-2" placeholder="Titre — utilise [mot](blue) ou [mot](pink) pour les highlights" value={cfg.banner.title || ""} onChange={(e) => updateBanner("title", e.target.value)} data-testid="banner-title" />
          <div className="sm:col-span-2 text-xs text-neutral-500">💡 Couleurs disponibles : <code>(blue)</code>, <code>(pink)</code>, <code>(yellow)</code>, <code>(green)</code>, <code>(black)</code>. Exemple : <code>L'école nous [concerne](blue). Faisons entendre [notre voix](pink).</code></div>
          <textarea className="neo-input sm:col-span-2 min-h-[80px]" placeholder="Sous-titre" value={cfg.banner.subtitle || ""} onChange={(e) => updateBanner("subtitle", e.target.value)} data-testid="banner-subtitle" />
          <input className="neo-input sm:col-span-2" placeholder="Texte CTA" value={cfg.banner.cta_label || ""} onChange={(e) => updateBanner("cta_label", e.target.value)} data-testid="banner-cta-label" />
          <input className="neo-input sm:col-span-2" placeholder="Lien CTA (/rejoindre, https://…)" value={cfg.banner.cta_link || ""} onChange={(e) => updateBanner("cta_link", e.target.value)} data-testid="banner-cta-link" />
          <div className="sm:col-span-2">
            <ImageUpload value={cfg.banner.image_url || ""} onChange={(url) => updateBanner("image_url", url)} label="Image de la bannière" testId="banner-image-upload" />
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase mb-4">Bulle flottante (Socials & Contact)</h2>
        <p className="text-sm text-neutral-600 mb-3">Laisse vide pour masquer le lien. WhatsApp est visible uniquement aux élèves connecté·es.</p>
        <div className="neo-card p-5 grid sm:grid-cols-2 gap-3" data-testid="site-fab-form">
          <input className="neo-input" placeholder="Instagram URL" value={cfg.fab.instagram_url || ""} onChange={(e) => updateFab("instagram_url", e.target.value)} data-testid="fab-instagram" />
          <input className="neo-input" placeholder="TikTok URL" value={cfg.fab.tiktok_url || ""} onChange={(e) => updateFab("tiktok_url", e.target.value)} data-testid="fab-tiktok" />
          <input className="neo-input" placeholder="Discord URL (invitation)" value={cfg.fab.discord_url || ""} onChange={(e) => updateFab("discord_url", e.target.value)} data-testid="fab-discord" />
          <input className="neo-input" placeholder="WhatsApp URL (membres)" value={cfg.fab.whatsapp_url || ""} onChange={(e) => updateFab("whatsapp_url", e.target.value)} data-testid="fab-whatsapp" />
          <input className="neo-input" placeholder="Email contact" value={cfg.fab.contact_email || ""} onChange={(e) => updateFab("contact_email", e.target.value)} data-testid="fab-email" />
          <input className="neo-input" placeholder="URL formulaire contact (optionnel)" value={cfg.fab.contact_form_url || ""} onChange={(e) => updateFab("contact_form_url", e.target.value)} data-testid="fab-contact-form" />
          <input className="neo-input sm:col-span-2" placeholder="URL d'adhésion au CEF officiel (utilisé sur les blocs « Rejoindre le CEF »)" value={cfg.fab.join_cef_url || ""} onChange={(e) => updateFab("join_cef_url", e.target.value)} data-testid="fab-join-cef-url" />
        </div>
      </section>

      <section>
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase mb-4">Catégories d'articles</h2>
        <p className="text-sm text-neutral-600 mb-3">Modifie ou supprime les catégories existantes, ou ajoute-en de nouvelles. Elles apparaissent dans le formulaire de publication d'article.</p>
        <div className="neo-card p-5 grid gap-2" data-testid="site-categories-form">
          {cats.map((c, idx) => (
            <div key={idx} className="flex gap-2 items-center" data-testid={`cat-row-${idx}`}>
              <input className="neo-input flex-1" value={c} onChange={(e) => updateCat(idx, e.target.value)} data-testid={`cat-input-${idx}`} />
              <button type="button" onClick={() => removeCat(idx)} className="neo-btn bg-cef-pink text-white text-sm py-2 px-3" aria-label="Supprimer" data-testid={`cat-del-${idx}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {cats.length === 0 && <div className="text-sm text-neutral-500">Aucune catégorie. Ajoute-en une ci-dessous.</div>}
          <div className="flex gap-2 mt-2 pt-3 border-t-2 border-black/10">
            <input className="neo-input flex-1" placeholder="Nouvelle catégorie…" value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCat())} data-testid="cat-new-input" />
            <button type="button" onClick={addCat} className="neo-btn bg-cef-blue text-white text-sm py-2" data-testid="cat-add"><Plus className="w-4 h-4" /> Ajouter</button>
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase mb-4">Outils rapides (page d'accueil)</h2>
        <p className="text-sm text-neutral-600 mb-3">Les 4 cartes cliquables affichées sur la home. Modifie le label, l'icône, la couleur et le lien (interne <code>/carte</code> ou externe <code>https://…</code>).</p>
        <div className="neo-card p-5 grid gap-3" data-testid="site-quick-access-form">
          {quickItems.map((q, idx) => (
            <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center" data-testid={`quick-row-${idx}`}>
              <input className="neo-input sm:col-span-4" placeholder="Label" value={q.label || ""} onChange={(e) => updateQuick(idx, "label", e.target.value)} data-testid={`quick-label-${idx}`} />
              <select className="neo-input sm:col-span-2" value={q.icon || "Sparkles"} onChange={(e) => updateQuick(idx, "icon", e.target.value)} data-testid={`quick-icon-${idx}`}>
                {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
              <select className="neo-input sm:col-span-2" value={q.color || "white"} onChange={(e) => updateQuick(idx, "color", e.target.value)} data-testid={`quick-color-${idx}`}>
                {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input className="neo-input sm:col-span-3" placeholder="/lien ou https://…" value={q.link || ""} onChange={(e) => updateQuick(idx, "link", e.target.value)} data-testid={`quick-link-${idx}`} />
              <button type="button" onClick={() => removeQuick(idx)} className="neo-btn bg-cef-pink text-white text-sm py-2 px-3 sm:col-span-1" aria-label="Supprimer" data-testid={`quick-del-${idx}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button type="button" onClick={addQuick} className="neo-btn bg-cef-blue text-white text-sm py-2 max-w-xs" data-testid="quick-add"><Plus className="w-4 h-4" /> Ajouter un outil</button>
        </div>
      </section>

      <section>
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase mb-4">En-têtes de pages</h2>
        <p className="text-sm text-neutral-600 mb-3">
          Pour chaque page publique, modifie le titre et le sous-titre affichés en haut. Utilise <code>[mot](blue)</code>, <code>[mot](pink)</code>, <code>[mot](yellow)</code>, <code>[mot](green)</code> ou <code>[mot](black)</code> pour mettre un mot en surbrillance colorée.
        </p>
        <div className="grid gap-3" data-testid="site-page-headers-form">
          {Object.entries(pageHeaders).map(([slug, hdr]) => (
            <div key={slug} className="neo-card p-4 grid gap-2" data-testid={`page-header-row-${slug}`}>
              <div className="flex items-center gap-2">
                <span className="chip bg-cef-yellow font-mono text-xs">/{slug === "actualites" ? "actualites" : slug === "avis" ? "donner-mon-avis" : slug}</span>
                <span className="text-xs text-neutral-500">page</span>
              </div>
              <input
                className="neo-input"
                placeholder="Titre — utilise [mot](color) pour les highlights"
                value={hdr.title || ""}
                onChange={(e) => updatePageHeader(slug, "title", e.target.value)}
                data-testid={`page-header-title-${slug}`}
              />
              <textarea
                className="neo-input min-h-[60px]"
                placeholder="Sous-titre"
                value={hdr.subtitle || ""}
                onChange={(e) => updatePageHeader(slug, "subtitle", e.target.value)}
                data-testid={`page-header-subtitle-${slug}`}
              />
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase mb-4">Thématiques FAQ</h2>
        <p className="text-sm text-neutral-600 mb-3">
          Les thématiques utilisées pour grouper les fiches FAQ sur la page « Tes droits ». Modifie, supprime ou ajoute.
        </p>
        <div className="neo-card p-5 grid gap-2" data-testid="site-faq-themes-form">
          {faqThemes.map((t, idx) => (
            <div key={idx} className="flex gap-2 items-center" data-testid={`faq-theme-row-${idx}`}>
              <input className="neo-input flex-1" value={t} onChange={(e) => updateFaqTheme(idx, e.target.value)} data-testid={`faq-theme-input-${idx}`} />
              <button type="button" onClick={() => removeFaqTheme(idx)} className="neo-btn bg-cef-pink text-white text-sm py-2 px-3" aria-label="Supprimer" data-testid={`faq-theme-del-${idx}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {faqThemes.length === 0 && <div className="text-sm text-neutral-500">Aucune thématique. Ajoute-en une ci-dessous.</div>}
          <div className="flex gap-2 mt-2 pt-3 border-t-2 border-black/10">
            <input className="neo-input flex-1" placeholder="Nouvelle thématique…" value={newFaqTheme} onChange={(e) => setNewFaqTheme(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFaqTheme())} data-testid="faq-theme-new-input" />
            <button type="button" onClick={addFaqTheme} className="neo-btn bg-cef-blue text-white text-sm py-2" data-testid="faq-theme-add"><Plus className="w-4 h-4" /> Ajouter</button>
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase mb-4">Bande défilante (marquee jaune)</h2>
        <p className="text-sm text-neutral-600 mb-3">Les messages qui défilent dans le bandeau jaune sous le hero. Tu peux ajouter, modifier, supprimer chaque ligne. Émojis bienvenus.</p>
        <div className="neo-card p-5 grid gap-2" data-testid="site-marquee-form">
          {marqueeItems.map((m, idx) => (
            <div key={`mq-${idx}`} className="flex gap-2 items-center" data-testid={`marquee-row-${idx}`}>
              <input className="neo-input flex-1" value={m} onChange={(e) => updateMarquee(idx, e.target.value)} data-testid={`marquee-input-${idx}`} />
              <button type="button" onClick={() => removeMarquee(idx)} className="neo-btn bg-cef-pink text-white text-sm py-2 px-3" aria-label="Supprimer" data-testid={`marquee-del-${idx}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {marqueeItems.length === 0 && <div className="text-sm text-neutral-500">Aucun message. Ajoute-en un ci-dessous.</div>}
          <div className="flex gap-2 mt-2 pt-3 border-t-2 border-black/10">
            <input className="neo-input flex-1" placeholder="Nouveau message défilant…" value={newMarquee} onChange={(e) => setNewMarquee(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addMarquee())} data-testid="marquee-new-input" />
            <button type="button" onClick={addMarquee} className="neo-btn bg-cef-blue text-white text-sm py-2" data-testid="marquee-add"><Plus className="w-4 h-4" /> Ajouter</button>
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase mb-4">Compteurs (page d'accueil)</h2>
        <p className="text-sm text-neutral-600 mb-3">Laisse vide pour utiliser le compteur automatique. Saisis un nombre pour forcer une valeur manuelle.</p>
        <div className="neo-card p-5 grid sm:grid-cols-3 gap-3" data-testid="site-stats-form">
          <label className="grid gap-1 text-sm font-bold">
            Élèves engagé·e·s
            <input className="neo-input" type="number" placeholder="auto" value={statsOv.students ?? ""} onChange={(e) => updateStatsOv("students", e.target.value === "" ? null : parseInt(e.target.value, 10))} data-testid="stats-override-students" />
          </label>
          <label className="grid gap-1 text-sm font-bold">
            Écoles touchées
            <input className="neo-input" type="number" placeholder="auto" value={statsOv.schools ?? ""} onChange={(e) => updateStatsOv("schools", e.target.value === "" ? null : parseInt(e.target.value, 10))} data-testid="stats-override-schools" />
          </label>
          <label className="grid gap-1 text-sm font-bold">
            Avis donnés
            <input className="neo-input" type="number" placeholder="auto" value={statsOv.voiced ?? ""} onChange={(e) => updateStatsOv("voiced", e.target.value === "" ? null : parseInt(e.target.value, 10))} data-testid="stats-override-voiced" />
          </label>
        </div>
      </section>

      <section>
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase mb-4">Blocs de la page d'accueil</h2>
        <p className="text-sm text-neutral-600 mb-3">Active ou masque chaque bloc de la page d'accueil. Décocher cache le bloc partout sur le site.</p>
        <div className="neo-card p-5 grid sm:grid-cols-2 gap-2 text-sm font-bold" data-testid="home-sections-form">
          {[
            ["stats", "Compteurs (élèves engagés, écoles touchées…)"],
            ["consultation", "Consultation du moment"],
            ["actions_upcoming", "Actions à venir"],
            ["featured_articles", "À la une (articles)"],
            ["quick_access", "Accès rapides (tuiles)"],
            ["social_feed", "Fil social (Insta / TikTok)"],
            ["page_blocks", "Blocs éditables (admin)"],
            ["legal_help", "Aide juridique (SDJ / InforJeunes)"],
            ["whatsapp", "Carte WhatsApp (élèves connecté·e·s)"],
            ["join_banner", "Bannière noire 'Agis avec nous'"],
            ["join_cef_block", "Bloc 'Rejoindre le CEF' (jaune)"],
          ].map(([k, label]) => (
            <label key={k} className="inline-flex items-center gap-2 cursor-pointer py-1">
              <input
                type="checkbox"
                checked={homeSections[k] !== false}
                onChange={(e) => updateHomeSection(k, e.target.checked)}
                data-testid={`home-section-${k}`}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase mb-4">Bloc « Consultation du moment »</h2>
        <p className="text-sm text-neutral-600 mb-3">Choisis quelle consultation est mise en avant + personnalise le titre, la description et le bouton. Tous les champs sont optionnels (les valeurs par défaut viennent de la consultation sélectionnée).</p>
        <div className="neo-card p-5 grid gap-3" data-testid="consult-block-form">
          <label className="grid gap-1 text-sm font-bold">Consultation à mettre en avant
            <select className="neo-input" value={consultBlock.consultation_slug || ""} onChange={(e) => updateConsultBlock("consultation_slug", e.target.value)} data-testid="consult-block-slug">
              <option value="">— Automatique (la dernière ouverte) —</option>
              {consultations.map((c) => (
                <option key={c.id} value={c.slug}>{c.title} ({c.status})</option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-sm font-bold">Titre personnalisé (laisser vide pour reprendre celui de la consultation)
            <input className="neo-input" value={consultBlock.custom_title || ""} onChange={(e) => updateConsultBlock("custom_title", e.target.value)} data-testid="consult-block-title" />
          </label>
          <label className="grid gap-1 text-sm font-bold">Description personnalisée
            <textarea className="neo-input min-h-[80px]" value={consultBlock.custom_description || ""} onChange={(e) => updateConsultBlock("custom_description", e.target.value)} data-testid="consult-block-description" />
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="grid gap-1 text-sm font-bold">Libellé du bouton
              <input className="neo-input" value={consultBlock.custom_cta_label || ""} onChange={(e) => updateConsultBlock("custom_cta_label", e.target.value)} placeholder="Donner mon avis" data-testid="consult-block-cta-label" />
            </label>
            <label className="grid gap-1 text-sm font-bold">Lien personnalisé (URL ou /chemin — laisser vide pour utiliser le lien par défaut)
              <input className="neo-input" value={consultBlock.custom_cta_link || ""} onChange={(e) => updateConsultBlock("custom_cta_link", e.target.value)} placeholder="https://… ou /donner-mon-avis/mon-slug" data-testid="consult-block-cta-link" />
            </label>
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase mb-4">Aide juridique (bas de la page d'accueil)</h2>
        <p className="text-sm text-neutral-600 mb-3">Bloc de partenaires juridiques. Chaque partenaire peut avoir une bulle d'info (pop-up) activable.</p>
        <div className="neo-card p-5 grid gap-4" data-testid="legal-help-form">
          <label className="flex items-center gap-2 text-sm font-bold">
            <input type="checkbox" checked={!!legal.enabled} onChange={(e) => updateLegal("enabled", e.target.checked)} data-testid="legal-enabled" /> Afficher le bloc
          </label>
          <label className="grid gap-1 text-sm font-bold">Titre du bloc
            <input className="neo-input" value={legal.title || ""} onChange={(e) => updateLegal("title", e.target.value)} data-testid="legal-title" />
          </label>
          <label className="grid gap-1 text-sm font-bold">Sous-titre
            <textarea className="neo-input min-h-[60px]" value={legal.subtitle || ""} onChange={(e) => updateLegal("subtitle", e.target.value)} data-testid="legal-subtitle" />
          </label>

          <div className="grid gap-3">
            {(legal.partners || []).map((p, idx) => (
              <div key={`lp-${idx}`} className="border-2 border-black rounded-md p-4 bg-cef-paper grid gap-2" data-testid={`legal-partner-edit-${idx}`}>
                <div className="flex items-center justify-between">
                  <div className="font-bold uppercase text-sm tracking-widest">Partenaire #{idx + 1}</div>
                  <button type="button" onClick={() => removeLegalPartner(idx)} className="neo-btn bg-cef-pink text-white text-xs py-1 px-2" data-testid={`legal-partner-del-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  <label className="grid gap-1 text-xs font-bold">Nom
                    <input className="neo-input" value={p.name || ""} onChange={(e) => updateLegalPartner(idx, "name", e.target.value)} data-testid={`legal-partner-${idx}-name`} />
                  </label>
                  <label className="grid gap-1 text-xs font-bold">Nom court
                    <input className="neo-input" value={p.short_name || ""} onChange={(e) => updateLegalPartner(idx, "short_name", e.target.value)} data-testid={`legal-partner-${idx}-short`} />
                  </label>
                  <label className="grid gap-1 text-xs font-bold">URL du site
                    <input className="neo-input" value={p.url || ""} onChange={(e) => updateLegalPartner(idx, "url", e.target.value)} data-testid={`legal-partner-${idx}-url`} />
                  </label>
                  <label className="grid gap-1 text-xs font-bold">URL du logo
                    <input className="neo-input" value={p.logo_url || ""} onChange={(e) => updateLegalPartner(idx, "logo_url", e.target.value)} data-testid={`legal-partner-${idx}-logo`} />
                  </label>
                  <label className="grid gap-1 text-xs font-bold">Libellé du bouton
                    <input className="neo-input" value={p.button_label || ""} onChange={(e) => updateLegalPartner(idx, "button_label", e.target.value)} data-testid={`legal-partner-${idx}-btnlabel`} />
                  </label>
                  <label className="flex items-center gap-2 text-xs font-bold mt-5">
                    <input type="checkbox" checked={!!p.popup_enabled} onChange={(e) => updateLegalPartner(idx, "popup_enabled", e.target.checked)} data-testid={`legal-partner-${idx}-popup-enabled`} /> Activer la pop-up
                  </label>
                </div>
                <label className="grid gap-1 text-xs font-bold">Contenu de la pop-up (texte libre, retours à la ligne respectés)
                  <textarea className="neo-input min-h-[120px]" value={p.popup_content || ""} onChange={(e) => updateLegalPartner(idx, "popup_content", e.target.value)} data-testid={`legal-partner-${idx}-popup-content`} />
                </label>
              </div>
            ))}
            <button type="button" onClick={addLegalPartner} className="neo-btn bg-cef-blue text-white text-sm py-2 w-fit" data-testid="legal-partner-add"><Plus className="w-4 h-4" /> Ajouter un partenaire</button>
          </div>
        </div>
      </section>

      <button onClick={save} className="neo-btn bg-cef-pink text-white max-w-xs" data-testid="site-config-save">Enregistrer la configuration</button>
    </div>
  );
}
